import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { LocalizationService } from 'app/core/localization/localization.service';
import { DistrictGameAccess } from 'app/parent/enums/district-game-access.enum';
import { CountryModel } from 'app/shared/models/country.model';
import { RegionModel } from 'app/shared/models/region.model';
import { LocationService } from 'app/shared/services/location.service';

@Component({
  selector: 'kh-district-form',
  templateUrl: './district-form.component.html',
  styleUrls: ['./district-form.component.less'],
  standalone: false
})
export class DistrictFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private locationService = inject(LocationService);
  private localizationService = inject(LocalizationService);
  private translationService = inject(TranslationService);

  @Input() public formTitle = 'District';
  @Input() public districtId?: string;
  @Input() public gameAccessSetting = false;
  @Input() public showButtonsRow = false;
  @Input() public displayContent = false;
  @Input() public isContentLoading = false;

  @Output() public clickSave = new EventEmitter<FormGroup>();

  public isLoading = true;

  public districtForm: FormGroup;
  public countries?: CountryModel[];
  public country?: CountryModel;
  public gameAccess = DistrictGameAccess;

  public errors: string[] = [];

  public districtFormFields = {
    abbrev: 'abbrev',
    boardName: 'boardName',
    countryCode: 'countryCode',
    dashboard: {
      isClassFilterEnabled: 'isClassFilterEnabled',
      isDistrictComparisonEnabled: 'isDistrictComparisonEnabled'
    },
    gameAccess: 'gameAccess',
    id: 'id',
    isHomeUpsellDisabled: 'isHomeUpsellDisabled',
    isParentUpsellEnabled: 'isParentUpsellEnabled',
    isPrivacyModeEnabled: 'isPrivacyModeEnabled',
    isValidated: 'isValidated',
    regionCode: 'regionCode'
  };

  public validationOptions = [true, false];

  public getCountryName = (country: CountryModel): string => country.Name;
  public getCountryCode = (country: CountryModel): string => country.CountryCode;
  public getRegionName = (region: RegionModel): string => region.Name;
  public getRegionCode = (region: RegionModel): string => region.RegionCode;
  public getValidationName = (isValidated: boolean): string =>
    isValidated
      ? this.translationService.instant('DISTRICT_FORM.VALIDATED')
      : this.translationService.instant('DISTRICT_FORM.NOT_VALIDATED');

  public constructor() {
    this.districtForm = this.createDistrictForm();
  }

  public ngOnInit(): void {
    this.locationService
      .getAllCountries(this.localizationService.getLocaleCode())
      .subscribe((countries: CountryModel[]) => {
        this.countries = countries;
        this.isLoading = false;
      });
  }

  public changeCountry(): void {
    if (this.districtForm.value.countryCode) {
      const selectedCountry = this.districtForm.value[this.districtFormFields.countryCode];
      this.country = this.countries?.find((country: CountryModel) => country.CountryCode === selectedCountry);
    }

    this.districtForm.get(this.districtFormFields.regionCode)?.setValue('');
  }

  public saveDistrictInfo(): void {
    this.clickSave.emit(this.districtForm);
  }

  private createDistrictForm(): FormGroup {
    const EMPTY_STRING = '';

    return this.formBuilder.group({
      [this.districtFormFields.id]: this.formBuilder.control(undefined),
      [this.districtFormFields.abbrev]: this.formBuilder.control(undefined),
      [this.districtFormFields.boardName]: this.formBuilder.control(undefined),
      [this.districtFormFields.countryCode]: this.formBuilder.control(EMPTY_STRING),
      [this.districtFormFields.regionCode]: this.formBuilder.control(EMPTY_STRING),
      [this.districtFormFields.gameAccess]: this.formBuilder.control(DistrictGameAccess.Open),
      [this.districtFormFields.isHomeUpsellDisabled]: this.formBuilder.control(false),
      [this.districtFormFields.isPrivacyModeEnabled]: this.formBuilder.control(false),
      [this.districtFormFields.isParentUpsellEnabled]: this.formBuilder.control(false),
      [this.districtFormFields.isValidated]: this.formBuilder.control(false),
      [this.districtFormFields.dashboard.isClassFilterEnabled]: this.formBuilder.control(false),
      [this.districtFormFields.dashboard.isDistrictComparisonEnabled]: this.formBuilder.control(false)
    });
  }
}
